import React, { useState, useContext, useEffect, useMemo } from "react";
import Footer from "../Components/Footer";
import Header from "../Components/Header";
import Pagetitle from "../Components/Pagetitle";
import { CartContext } from "../CartContext";
import { useNavigate } from "react-router-dom";
import { FaCheckCircle } from "react-icons/fa";
import ReactPixel from "react-facebook-pixel";
import { gtag_report_conversion } from "../utils/gtag";
import AuthContext from "../AuthContext";
import { postData, getData } from "../Api";
import { SettingsContext } from "../SettingsContext";

const options = { autoConfig: true, debug: false };
ReactPixel.init("419304844367207", options);
ReactPixel.pageView();

function Checkout() {
  const { settings } = useContext(SettingsContext);
  const { user, login } = useContext(AuthContext);
  const { cart, setCart } = useContext(CartContext);
  const navigate = useNavigate();

  const [selectedPayment, setSelectedPayment] = useState("cod");
  const [showModal, setShowModal] = useState(false);
  const [showModal2, setShowModal2] = useState(false);
  const [showEmptyCartModal, setShowEmptyCartModal] = useState(false);
  const [errors, setErrors] = useState({});
  const [streetOptions, setStreetOptions] = useState([]);
  const [showAirpayPopup, setShowAirpayPopup] = useState(false);
  const [airpayUrl, setAirpayUrl] = useState("");
  const [airpayPayload, setAirpayPayload] = useState({});
  const [loadingPincode, setLoadingPincode] = useState(false);
  const [paymentError, setPaymentError] = useState({
    show: false,
    message: "",
  });
  const [airpayOrderId, setAirpayOrderId] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState("pending");

  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    address: "",
    landmark: "",
    pincode: "",
    city: "",
    state: "",
    street: "",
    country: "India",
  });

  /* 🔒 SAFE PRICE + QTY HELPERS (CORE FIX) */

  const getItemPrice = (item) => Number(item.discount_price);

  const getItemQty = (item) => Number(item.quantity || 1);

  /* 🧮 SUBTOTAL & TOTAL (SINGLE SOURCE OF TRUTH) */

  const originalSubtotal = cart.reduce(
    (sum, item) => sum + item.price * item.quantity,
    0,
  );

  const discountedSubtotal = cart.reduce(
    (sum, item) => sum + item.discount_price * item.quantity,
    0,
  );

  const discountAmount = originalSubtotal - discountedSubtotal;

  const subtotal = useMemo(() => {
    return cart.reduce((sum, item) => {
      return sum + getItemPrice(item) * getItemQty(item);
    }, 0);
  }, [cart]);

  const shippingCharge =
    cart.length === 1 && getItemQty(cart[0]) === 1 ? 100 : 0;

  const totalAmount = subtotal + shippingCharge;

  /* 🏠 ADDRESS HELPERS */

  const fetchPincodeDetails = async (pincode) => {
    if (pincode.length !== 6) return;

    setLoadingPincode(true);

    try {
      const data = await getData(`/city/${pincode}`);
      if (data.status) {
        setStreetOptions(data.streets || []);
        setFormData((p) => ({
          ...p,
          city: data.city,
          state: data.state,
        }));
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingPincode(false);
    }
  };

  const fetchAddress = async (phone) => {
    try {
      const { success, userdetail } = await postData("/fetchShipAdd", {
        phone,
      });
      if (success && userdetail) {
        setFormData((p) => ({ ...p, ...userdetail }));
      }
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    if (user?.phone) {
      setFormData((p) => ({ ...p, phone: user.phone }));
      fetchAddress(user.phone);
    }
  }, [user]);

  useEffect(() => {
    if (formData.pincode.length === 6) {
      fetchPincodeDetails(formData.pincode);
    } else {
      setStreetOptions([]);
    }
  }, [formData.pincode]);

  const showPaymentError = (message) => {
    setPaymentError({
      show: true,
      message,
    });
  };

  /* ✍️ FORM HANDLING */

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));

    if (name === "phone" && value.length === 10) fetchAddress(value);
  };

  const handleValidation = () => {
    let errs = {};

    const requiredFields = ["name", "phone", "address", "landmark", "pincode"];

    requiredFields.forEach((field) => {
      if (!formData[field]?.toString().trim()) {
        errs[field] = `Please enter ${field}`;
      }
    });

    if (streetOptions.length > 0 && !formData.street) {
      errs.street = "Please select street";
    }

    setErrors(errs);

    if (Object.keys(errs).length > 0) {
      setShowModal2(true);
      return false;
    }

    return true;
  };

  /* 🛒 SUBMIT ORDER */

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!cart.length) {
      setShowEmptyCartModal(true);
      return;
    }

    if (!handleValidation()) return;

    const paymentMethod =
      selectedPayment === "prepaid-razorpay"
        ? "razorpay"
        : selectedPayment === "prepaid-airpay"
          ? "airpay"
          : "cod";

    const frozenCart = cart.map((i) => ({
      ...i,
      quantity: Number(i.quantity || 1),
      discount_price: Number(i.discount_price),
    }));

    try {
      const data = await postData("/checkout", {
        ...formData,
        cartItems: frozenCart,
        totalAmount,
        paymentMethod,
        order_source: "web",
      });

      if (!data.success) throw new Error("Checkout failed");

      if (paymentMethod === "razorpay") {
        initiateRazorpayPayment(data);
        return;
      }

      if (paymentMethod === "airpay") {
        setAirpayUrl(data.iframe_url);
        setAirpayPayload(data.payload);
        setAirpayOrderId(data.payload.orderid);

        setPaymentStatus("pending");
        setShowAirpayPopup(true);

        setTimeout(() => {
          document.getElementById("airpay_form")?.submit();
        }, 300);

        return;
      }

      handleSuccess();
    } catch (err) {
      console.error(err);
      alert("Order failed. Please try again.");
    }
  };

  /* 💳 RAZORPAY */

  const initiateRazorpayPayment = (data) => {
    const rzp = new window.Razorpay({
      key: "rzp_live_XHwNz36Nd8LOQR",
      amount: data.amount,
      currency: data.currency,
      name: "Color Kingdon",
      order_id: data.razorpay_order_id,

      handler: async (res) => {
        try {
          const verify = await postData("/verify-payment", {
            razorpay_payment_id: res.razorpay_payment_id,
            razorpay_order_id: res.razorpay_order_id,
            razorpay_signature: res.razorpay_signature,
          });

          if (verify.success) {
            handleSuccess();
          } else {
            showPaymentError(
              "Payment verification failed. Amount not deducted.",
            );
          }
        } catch {
          showPaymentError(
            "Payment verification error. Please contact support.",
          );
        }
      },

      modal: {
        ondismiss: () => {
          showPaymentError("Payment cancelled by user.");
        },
      },

      prefill: {
        name: formData.name,
        contact: formData.phone,
      },
    });

    rzp.open();
  };

  useEffect(() => {
    if (!showAirpayPopup || !airpayOrderId) return;

    let attempts = 0;
    let interval;

    const startVerify = () => {
      interval = setInterval(async () => {
        attempts++;

        const res = await getData(`/verify-airpay-order/${airpayOrderId}`);

        if (res?.status === "success") {
          clearInterval(interval);
          setShowAirpayPopup(false);
          handleSuccess();
        }

        if (res?.status === "failed" && attempts >= 10) {
          clearInterval(interval);
          setShowAirpayPopup(false);
          setPaymentError({
            show: true,
            message: "Payment failed. Amount not deducted.",
          });
        }

        if (attempts >= 30) {
          clearInterval(interval);
          setShowAirpayPopup(false);
          setPaymentError({
            show: true,
            message:
              "Payment is processing. If amount is deducted, it will be confirmed shortly.",
          });
        }
      }, 5000);
    };

    const delay = setTimeout(startVerify, 12000);

    return () => {
      clearTimeout(delay);
      if (interval) clearInterval(interval);
    };
  }, [showAirpayPopup, airpayOrderId]);

  /* ✅ SUCCESS */

  const handleSuccess = () => {
    setShowModal(true);
    login({ name: formData.name, phone: formData.phone });

    ReactPixel.track("Purchase", { value: totalAmount, currency: "INR" });
    gtag_report_conversion({ value: totalAmount, currency: "INR" });

    setCart([]);
  };

  return (
    <>
      <main className="container-fluid">
        <Header />
        <Pagetitle />

        <form onSubmit={handleSubmit} className="row px-sm-5 py-5">
          <div className="col-md-8">
            <h4>Billing Details</h4>

            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">Phone number</label>
                <input
                  type="tel"
                  className={`form-control py-3 ${errors.phone ? "error" : ""}`}
                  name="phone"
                  placeholder="Phone number"
                  value={formData.phone}
                  maxLength={10}
                  pattern="[0-9]{10}"
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, "");
                    if (value.length <= 10) {
                      setFormData((p) => ({ ...p, phone: value }));
                      if (value.length === 10) fetchAddress(value);
                    }
                  }}
                  onBlur={handleChange}
                />
                {errors.phone && (
                  <div className="error-text">{errors.phone}</div>
                )}
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label">Name</label>
                <input
                  type="text"
                  className={`form-control py-3 ${errors.name ? "error" : ""}`}
                  name="name"
                  placeholder="Name"
                  value={formData.name}
                  onChange={handleChange}
                />
                {errors.name && <div className="error-text">{errors.name}</div>}
              </div>
            </div>

            <div className="row">
              {/* <div className="col-md-6 mb-3">
                <label className="form-label">Email Address</label>
                <input
                  type="email"
                  className={`form-control py-3 ${errors.email ? "error" : ""}`}
                  name="email"
                  placeholder="Email Address"
                  value={formData.email}
                  onChange={handleChange}
                />
                {errors.email && (
                  <div className="error-text">{errors.email}</div>
                )}
              </div> */}

              <div className="col-md-12 mb-3">
                <label className="form-label">Full Address</label>
                <textarea
                  className={`form-control ${errors.address ? "error" : ""}`}
                  name="address"
                  rows="3"
                  placeholder="Enter your full address"
                  value={formData.address}
                  onChange={handleChange}
                />
                {errors.address && (
                  <div className="error-text">{errors.address}</div>
                )}
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label">Landmark</label>
                <textarea
                  className={`form-control ${errors.landmark ? "error" : ""}`}
                  name="landmark"
                  rows="2"
                  placeholder="Nearby temple, school, shop"
                  value={formData.landmark}
                  onChange={handleChange}
                />
                {errors.landmark && (
                  <div className="error-text">{errors.landmark}</div>
                )}
              </div>

              <div className="col-md-3 mb-3">
                <label className="form-label">Pincode</label>
                <input
                  type="text"
                  name="pincode"
                  className={`form-control py-3 ${
                    errors.pincode ? "error" : ""
                  }`}
                  placeholder="Pincode"
                  inputMode="numeric"
                  maxLength={6}
                  value={formData.pincode}
                  onChange={(e) => {
                    const digits = e.target.value.replace(/\D/g, "");
                    setFormData((p) => ({ ...p, pincode: digits }));
                  }}
                />
                {errors.pincode && (
                  <div className="error-text">{errors.pincode}</div>
                )}
              </div>

              <div className="col-md-3 mb-3">
                <label className="form-label">Street / Area</label>

                <select
                  name="street"
                  className="form-control py-3"
                  value={formData.street}
                  onChange={handleChange}
                >
                  <option value="">Select Street / Area</option>
                  {streetOptions.map((s, idx) => (
                    <option key={idx} value={s.street}>
                      {s.street}
                    </option>
                  ))}
                </select>

                {errors.street && (
                  <div className="error-text">{errors.street}</div>
                )}
              </div>
            </div>

            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">City</label>
                <input
                  type="text"
                  className={`form-control py-3 ${errors.city ? "error" : ""}`}
                  name="city"
                  placeholder="City"
                  value={formData.city}
                  onChange={handleChange}
                />
                {errors.city && <div className="error-text">{errors.city}</div>}
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label">State</label>
                <input
                  type="text"
                  className={`form-control py-3 ${errors.state ? "error" : ""}`}
                  name="state"
                  placeholder="State"
                  value={formData.state}
                  onChange={handleChange}
                />
                {errors.state && (
                  <div className="error-text">{errors.state}</div>
                )}
              </div>
            </div>
          </div>

          <div className="col-md-4">
            <div className="card p-3">
              <h5>In Your Cart ({cart.length})</h5>

              {cart.map((item) => (
                <div key={item.id} className="d-flex align-items-center mb-3">
                  <img
                    src={item.image}
                    alt={item.title}
                    style={{ width: "80px", height: "80px", cursor: "pointer" }}
                    className="me-3 rounded"
                    onClick={() => navigate(`/product/${item.slug}`)}
                  />

                  <div>
                    <p className="mb-1 fw-semibold text-dark">{item.title}</p>

                    <p className="mb-1 d-flex align-items-center flex-wrap gap-2">
                      <del className="text-muted small">₹{item.price}</del>

                      <span className="badge bg-danger-subtle text-danger fw-bold">
                        ₹{item.discount_price}
                      </span>

                      <span className="text-muted small">
                        × {item.quantity}
                      </span>
                    </p>

                    <p className="fw-bold text-success mb-0">
                      ₹{item.quantity * item.discount_price}
                    </p>
                  </div>
                </div>
              ))}

              <hr />
              {/* MRP Subtotal */}
              <p className="d-flex justify-content-between">
                <span>MRP Subtotal</span>
                <span className="text-muted fw-bold">₹{originalSubtotal}</span>
              </p>

              {/* Discounted Subtotal */}
              <p className="d-flex justify-content-between mb-0">
                <span>Discounted Subtotal</span>
                <span className="fw-bold text-success">
                  ₹{discountedSubtotal}
                </span>
              </p>

              {/* You Saved */}
              {discountAmount > 0 && (
                <p className="text-success small mb-1">
                  <i className="fa-solid fa-tag"></i> You saved ₹
                  {discountAmount} on this order
                </p>
              )}

              {/* Shipping */}
              <p className="d-flex justify-content-between mb-0">
                <span>Shipping</span>
                <span>₹{shippingCharge}</span>
              </p>

              <hr />

              {/* Final Total */}
              <p className="d-flex justify-content-between fs-5 fw-bold mb-0">
                <span>Total Payable</span>
                <span>₹{totalAmount}</span>
              </p>
            </div>

            {/* Payment Methods */}
            <div className="pt-4">
              <h4 className="mb-3">Select Payment Method</h4>

              <div className="payment-method-container">
                {/* COD */}
                {settings?.cod == true && (
                  <div
                    className={`payment-card ${
                      selectedPayment === "cod" ? "active" : ""
                    }`}
                    onClick={() => setSelectedPayment("cod")}
                  >
                    <div className="payment-icon">
                      <i className="fas fa-truck"></i>
                    </div>

                    <div className="payment-info">
                      <h6 className="m-0 fw-bold">Cash on Delivery</h6>
                      <small className="text-muted">
                        Pay when your order arrives
                      </small>
                    </div>
                  </div>
                )}

                {/* SHOW ONLINE PAYMENT ONLY IF ANY PREPAID OPTION ENABLED */}
                {(settings?.razorpay == true || settings?.airpay == true) && (
                  <div
                    className={`payment-card ${
                      selectedPayment.includes("prepaid") ? "active" : ""
                    }`}
                    onClick={() =>
                      setSelectedPayment(
                        settings?.razorpay
                          ? "prepaid-razorpay"
                          : "prepaid-airpay",
                      )
                    }
                  >
                    <div className="payment-icon online-icon">
                      <i className="fas fa-credit-card"></i>
                    </div>

                    <div className="payment-info">
                      <h6 className="m-0 fw-bold">Online Payment</h6>
                      <small className="text-muted">
                        UPI, Cards & Netbanking
                      </small>
                    </div>
                  </div>
                )}
              </div>

              {/* Prepaid Sub-Options */}
              {selectedPayment.includes("prepaid") &&
                (settings?.razorpay || settings?.airpay) && (
                  <div className="prepaid-options-box mt-3">
                    {/* Razorpay */}
                    {settings?.razorpay == true && (
                      <div
                        className={`prepaid-option ${
                          selectedPayment === "prepaid-razorpay"
                            ? "selected"
                            : ""
                        }`}
                        onClick={() => setSelectedPayment("prepaid-razorpay")}
                      >
                        <img
                          src="https://upload.wikimedia.org/wikipedia/commons/8/89/Razorpay_logo.svg"
                          className="payment-logo"
                          alt="Razorpay"
                        />
                      </div>
                    )}

                    {/* Airpay */}
                    {settings?.airpay == true && (
                      <div
                        className={`prepaid-option ${
                          selectedPayment === "prepaid-airpay" ? "selected" : ""
                        }`}
                        onClick={() => setSelectedPayment("prepaid-airpay")}
                      >
                        <img
                          src="https://airpay.co.in/resources/images/airpay-text.svg"
                          className="payment-logo"
                          alt="Airpay"
                        />
                      </div>
                    )}
                  </div>
                )}

              <button
                type="submit"
                className="btn-style1 w-100 mt-4"
                disabled={loadingPincode}
              >
                {loadingPincode ? "Loading address..." : "Place Order"}
              </button>
            </div>
          </div>
        </form>

        <Footer />
      </main>

      <div className={`modal fade ${showModal ? "show d-block" : ""}`}>
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content py-4 rounded-5">
            <div className="modal-body text-center">
              <FaCheckCircle size={60} color="green" />
              <h2 className="py-2">
                आपका ऑर्डर सफलतापूर्वक हो गया है! 5–7 दिन में आपका ऑर्डर डिलीवर
                हो जाएगा
              </h2>
              <button className="btn-style1 mt-3" onClick={() => navigate("/")}>
                Go to Home
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className={`modal fade ${showModal2 ? "show d-block" : ""}`}>
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content py-4 rounded-5">
            <div className="modal-body text-center">
              <FaCheckCircle size={60} color="orange" />
              <h2 className="py-2">कृप्या पहले पूरा फॉर्म भरे</h2>
              <button
                className="btn-style1 mt-3"
                onClick={() => setShowModal2(false)}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className={`modal fade ${showEmptyCartModal ? "show d-block" : ""}`}>
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content py-4 rounded-5">
            <div className="modal-body text-center">
              <FaCheckCircle size={60} color="orange" />
              <h2 className="py-2">Your cart is empty!</h2>
              <button
                className="btn-style1 mt-3"
                onClick={() => {
                  setShowEmptyCartModal(false);
                  navigate("/");
                }}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      </div>

      {paymentError.show && (
        <div className="modal fade show d-block">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content py-4 rounded-5">
              <div className="modal-body text-center">
                <FaCheckCircle
                  size={60}
                  color={
                    paymentError.message.includes("processing")
                      ? "orange"
                      : "red"
                  }
                />
                <h2 className="py-2">{paymentError.message}</h2>
                <button
                  className="btn-style1 mt-3"
                  onClick={() => setPaymentError({ show: false, message: "" })}
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showAirpayPopup && (
        <div
          className="airpay-popup-overlay"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            background: "rgba(0,0,0,0.65)",
            zIndex: 99999,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <div
            className="airpay-popup-box"
            style={{
              width: "90%",
              maxWidth: "600px",
              background: "white",
              borderRadius: "10px",
              overflow: "hidden",
              position: "relative",
            }}
          >
            {/* CLOSE BUTTON */}
            <button
              onClick={() => {
                setShowAirpayPopup(false);
                setPaymentError({
                  show: true,
                  message:
                    "Payment window closed. If amount is deducted, it will be confirmed shortly.",
                });
              }}
              className="airpay-close-btn"
            >
              <i className="fas fa-times"></i>
            </button>

            <form
              id="airpay_form"
              action={airpayUrl}
              method="POST"
              target="airpay_frame"
            >
              {Object.entries(airpayPayload).map(([key, value]) => (
                <input key={key} type="hidden" name={key} value={value} />
              ))}
            </form>

            <iframe
              name="airpay_frame"
              title="Airpay Payment"
              style={{
                width: "100%",
                height: "98vh",
                border: "none",
              }}
            />
          </div>
        </div>
      )}
    </>
  );
}

export default Checkout;
